import SentryService from "./sentryService";
import PaymentPermissionService from "./paymentPermissionService";

const PaymentService = {
  url: `${process.env.VUE_APP_API_URL}payments/`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },

  create: async (data) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}create`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: headers,
      });

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "payment.create",
        data: JSON.stringify(data),
      });
      return false;
    }
  },

  createOnReservedCreditLine: async (data) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}create-on-reserved-credit-line`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      return false;
    }
  },

  get: async (options) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const params = new URLSearchParams(options);

      const response = await fetch(
        `${process.env.VUE_APP_API_URL}payments?${params}`,
        {
          headers: headers,
        }
      );
      return response;
    } catch (e) {
      return false;
    }
  },

  getResume: async (user) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}resume`, {
        body: JSON.stringify(user),
        headers: headers,
      });
      return response;
    } catch (e) {
      return false;
    }
  },

  confirmPayment: async (payment_id, payment_status) => {
    try {
      // Verificar permiso antes de proceder - ahora payment_status es verificado dentro de canPerformActions
      if (!PaymentPermissionService.canPerformActions({ payment_status })) {
        SentryService.captureMessage(
          "Intento bloqueado de confirmar pago en estado expirado",
          "warning",
          {
            payment_id: payment_id,
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}confirm-payment`, {
        method: "POST",
        body: JSON.stringify({ payment_id }),
        headers: headers,
      });

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "payment.confirm",
        payment_id,
      });
      return false;
    }
  },
  rejectPayment: async (payment_id, payment_status) => {
    try {
      // Verificar permiso antes de proceder - usando el nuevo método mejorado
      if (!PaymentPermissionService.canPerformActions({ payment_status })) {
        SentryService.captureMessage(
          "Intento bloqueado de rechazar pago en estado expirado",
          "warning",
          {
            payment_id: payment_id,
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}reject-payment`, {
        method: "POST",
        body: JSON.stringify({ payment_id }),
        headers: headers,
      });

      return response;
    } catch (e) {
      return false;
    }
  },

  reportNonPayment: async (payment_id) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}report-non-payment`, {
        method: "POST",
        body: JSON.stringify({ payment_id }),
        headers: headers,
      });

      return response;
    } catch (e) {
      return false;
    }
  },

  reportPayment: async (data) => {
    try {
      // Verificar permiso antes de proceder - la verificación de data.payment_status ahora se hace internamente
      if (
        !PaymentPermissionService.canPerformActions({
          payment_status: data.payment_status,
        })
      ) {
        SentryService.captureMessage(
          "Intento bloqueado de reportar pago en estado expirado",
          "warning",
          {
            payment_id: data.payment_id,
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      // Registrar inicio del proceso de envío de comprobante
      SentryService.captureMessage(
        "Inicio de envío de comprobante de pago",
        "info",
        {
          payment_id: data.payment_id,
          file_type: data.file ? data.file.type : null,
          file_size: data.file ? data.file.size : null,
        }
      );

      var myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append(
        "Authorization",
        `Bearer ${localStorage.getItem("token")}`
      );

      var formdata = new FormData();

      for (let prop in data) {
        formdata.append(prop, data[prop]);
      }

      var requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      const response = await fetch(
        `${PaymentService.url}report-payment`,
        requestOptions
      );

      // Registrar respuesta del servidor
      if (!response.ok) {
        SentryService.captureMessage(
          "Error en respuesta del servidor al reportar pago",
          "error",
          {
            payment_id: data.payment_id,
            status: response.status,
            statusText: response.statusText,
          }
        );
      }

      return response;
    } catch (e) {
      // Capturar detalles del error
      SentryService.captureError(e, {
        action: "payment.reportPayment",
        payment_id: data.payment_id,
        file_info: data.file
          ? {
              name: data.file.name,
              type: data.file.type,
              size: data.file.size,
              lastModified: data.file.lastModified,
            }
          : "No file provided",
      });

      console.error("Error en reportPayment:", e);
      return false;
    }
  },

  generateProofPurchase: async (paymentId) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}generate-proof-purchase/${paymentId}`,
        {
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      console.log(e);
      return false;
    }
  },
  extendTerm: async (payment_id, term, payment_status) => {
    try {
      // Verificar permiso antes de proceder - usando el método mejorado que verifica payment_status
      if (!PaymentPermissionService.canPerformActions({ payment_status })) {
        SentryService.captureMessage(
          "Intento bloqueado de extender plazo de pago en estado expirado",
          "warning",
          {
            payment_id: payment_id,
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}extend-term`, {
        method: "POST",
        body: JSON.stringify({ payment_id, term }),
        headers: headers,
      });

      return response;
    } catch (e) {
      return false;
    }
  },

  attachInvoice: async (formdata) => {
    try {
      // Verificar permiso antes de proceder - usando el método mejorado
      const payment_status = formdata.get("payment_status");
      if (!PaymentPermissionService.canPerformActions({ payment_status })) {
        SentryService.captureMessage(
          "Intento bloqueado de adjuntar factura a pago en estado expirado",
          "warning",
          {
            payment_id: formdata.get("payment_id"),
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      var myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append(
        "Authorization",
        `Bearer ${localStorage.getItem("token")}`
      );

      var requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      const response = await fetch(
        `${PaymentService.url}attach-invoice`,
        requestOptions
      );
      return response;
    } catch (e) {
      return false;
    }
  },

  requestExtendTerm: async (payment_id, days) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(`${PaymentService.url}request-extend-term`, {
        method: "POST",
        body: JSON.stringify({ payment_id, days }),
        headers: headers,
      });

      return response;
    } catch (e) {
      return false;
    }
  },

  handleRequestExtendTerm: async (term_extension_request_id, accepted) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}handle-request-extend-term`,
        {
          method: "POST",
          body: JSON.stringify({ term_extension_request_id, accepted }),
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      return false;
    }
  },

  getTermExtensionRequest: async (accepted = "all", page = 1, order = "") => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}get-term-extensions?accepted=${accepted}&page=${page}&order=${order}`,
        {
          headers: headers,
        }
      );
      return response;
    } catch (e) {
      return false;
    }
  },

  getTotalPaymentsByUser: async () => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}get-total-payments-by-user`,
        {
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      return false;
    }
  },

  updatePayment: async (payment_id, data, payment_status) => {
    try {
      // Verificar permiso antes de proceder - usando el método mejorado
      if (!PaymentPermissionService.canPerformActions({ payment_status })) {
        SentryService.captureMessage(
          "Intento bloqueado de actualizar pago en estado expirado",
          "warning",
          {
            payment_id: payment_id,
          }
        );

        // Simular una respuesta de error
        return new Response(
          JSON.stringify({
            success: false,
            msg: "No se pueden realizar acciones en pagos expirados",
          }),
          {
            status: 403,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}update-payment/${payment_id}`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      return false;
    }
  },

  async reportPayments(exportOption) {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");
      console.log(`${PaymentService.url}report-payments`);
      const response = await fetch(`${PaymentService.url}customers-report`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ exportOption }),
      });

      if (!response.ok) {
        const errorDetails = await response.json();
        throw new Error(errorDetails.msg || "Error al generar el reporte");
      }

      return response;
    } catch (error) {
      SentryService.captureError(error, {
        action: "payment.reportPayments",
        exportOption,
      });
      console.error("Error en PaymentService.reportPayments:", error);
      throw error;
    }
  },

  async exportData(exportOption, startDate, endDate, ownerId) {
    const fullUrl = `${this.url}export`;
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({
        exportOption,
        startDate,
        endDate,
        owner_id: ownerId,
      }),
    });

    if (!response.ok) {
      const errorDetails = await response.json();
      throw new Error(errorDetails.msg || "Error en la exportación");
    }

    return response;
  },

  assignProjectToPayment: async (data) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}assign-project-to-payment`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "payment.assignProjectToPayment",
        data: JSON.stringify(data),
      });
      console.error("Error en PaymentService:", e);
      return false;
    }
  },

  getPaymentsToProject: async (data) => {
    try {
      const params = new URLSearchParams(data);
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const response = await fetch(
        `${PaymentService.url}get-payments-to-projects?${params}`,
        {
          headers: headers,
        }
      );

      return response;
    } catch (e) {
      return false;
    }
  },
  requestFactoring: async (payment_id) => {
    try {
      const headers = PaymentService.headers;
      headers["Authorization"] = "Bearer " + localStorage.getItem("token");

      const data = { payment_id: payment_id };

      const response = await fetch(`${PaymentService.url}request-factoring`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: headers,
      });

      return response;
    } catch (e) {
      return false;
    }
  },

  getStpClabe: async (paymentId) => {
    try {
      // Create new headers object to avoid mutation
      const token = localStorage.getItem("token");
      const headers = {
        ...PaymentService.headers,
      };

      if (token) {
        headers["Authorization"] = "Bearer " + token;
      }

      const response = await fetch(
        `${PaymentService.url}get-stp-clabe/${paymentId}`,{ headers }
      );

      return response;
    } catch (e) {
      SentryService.captureError(e, {
        action: "payment.getStpClabe",
        paymentId,
      });
      
      // Return simulated error response instead of false
      return new Response(
        JSON.stringify({
          success: false,
          msg: "Error de red al obtener CLABE STP",
        }),
        {
          status: 500,
          headers: { "Content-Type": "application/json" },
        }
      );
    }
  },
};

export default PaymentService;
